import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/apiConfig';

interface InvoiceData {
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string | null;
    clientName: string;
    clientAddress: string | null;
    clientPhone: string | null;
    clientGstin: string | null;
    subtotal: number;
    discountPercent: number;
    discountAmount: number;
    taxPercent: number;
    taxAmount: number;
    totalAmount: number;
    status: string;
    invoiceType: string;
    notes: string | null;
    termsAndConditions: string | null;
    businessSetting: {
        businessName: string;
        invoicePrefix: string;
        gstin: string | null;
        addressLine1: string | null;
        addressLine2: string | null;
        addressLine3: string | null;
        email: string | null;
        phoneNumber: string | null;
    };
    lineItems: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
        amount: number;
    }>;
}

interface Props {
    invoiceId: string;
    token: string;
    onClose: () => void;
}

export default function InvoiceView({ invoiceId, token, onClose }: Props) {
    const [invoice, setInvoice] = useState<InvoiceData | null>(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchInvoice();
    }, [invoiceId]);

    const fetchInvoice = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${invoiceId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch invoice');

            const data = await response.json();
            setInvoice(data.data);
        } catch (error) {
            console.error('Error fetching invoice:', error);
            toast.error('Failed to fetch invoice');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        if (!printRef.current) return;

        // Use browser's print functionality
        window.print();
    };

    const getDocumentTitle = () => {
        if (!invoice) return 'Invoice';
        if (invoice.invoiceType === 'po' || invoice.status === 'PO') {
            return 'PURCHASE ORDER';
        }
        return 'TAX INVOICE';
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-gray-600">Loading invoice...</p>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
                <div className="text-center">
                    <p className="text-red-600">Invoice not found</p>
                    <button
                        onClick={onClose}
                        className="mt-4 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Print Styles */}
            <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .page-break {
            page-break-after: always;
          }
        }
      `}</style>

            <div className="fixed inset-0 bg-gray-50 overflow-auto z-50">
                {/* Action Bar - No Print */}
                <div className="no-print bg-white shadow-sm sticky top-0 z-10">
                    <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                        >
                            ← Back to List
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={handleDownloadPDF}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>

                {/* Invoice Content */}
                <div className="max-w-4xl mx-auto p-8">
                    <div
                        id="invoice-print-area"
                        ref={printRef}
                        className="bg-white shadow-lg rounded-lg p-8"
                        style={{ minHeight: '297mm' }}
                    >
                        {/* Header & Info Section */}
                        <div className="border-b-2 border-gray-800 pb-4 mb-4">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                                        {invoice.businessSetting.businessName}
                                    </h1>
                                </div>
                                <div className="text-right">
                                    <h2 className="text-xl font-bold text-indigo-600 leading-tight">
                                        {getDocumentTitle()}
                                    </h2>
                                    <div className="text-sm font-medium text-gray-500 mt-1">#{invoice.invoiceNumber}</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-8">
                                {/* Billed By */}
                                <div className="text-sm">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Billed By</h3>
                                    <div className="text-gray-900 font-semibold">{invoice.businessSetting.businessName}</div>
                                    <div className="text-gray-600 leading-snug">
                                        {invoice.businessSetting.addressLine1 && <div>{invoice.businessSetting.addressLine1}</div>}
                                        {invoice.businessSetting.addressLine2 && <div>{invoice.businessSetting.addressLine2}</div>}
                                        {invoice.businessSetting.addressLine3 && <div>{invoice.businessSetting.addressLine3}</div>}

                                        <div className="mt-2 space-y-0.5">
                                            {invoice.businessSetting.gstin && (
                                                <div><span className="font-medium text-gray-700">GSTIN:</span> {invoice.businessSetting.gstin}</div>
                                            )}
                                            {invoice.businessSetting.email && (
                                                <div><span className="font-medium text-gray-700">Email:</span> {invoice.businessSetting.email}</div>
                                            )}
                                            {invoice.businessSetting.phoneNumber && (
                                                <div><span className="font-medium text-gray-700">Phone:</span> {invoice.businessSetting.phoneNumber}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Billed To & Invoice Details */}
                                <div className="flex flex-col justify-between">
                                    <div className="text-sm mb-4">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Billed To</h3>
                                        <div className="text-gray-900 font-semibold">{invoice.clientName}</div>
                                        <div className="text-gray-600 leading-snug">
                                            {invoice.clientAddress && <div className="whitespace-pre-line">{invoice.clientAddress}</div>}
                                            <div className="mt-1 space-y-0.5">
                                                {invoice.clientPhone && (
                                                    <div><span className="font-medium text-gray-700">Phone:</span> {invoice.clientPhone}</div>
                                                )}
                                                {invoice.clientGstin && (
                                                    <div><span className="font-medium text-gray-700">GSTIN:</span> {invoice.clientGstin}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-8 text-sm border-t border-gray-100 pt-3">
                                        <div>
                                            <div className="text-xs text-gray-500 font-medium uppercase">Date</div>
                                            <div className="text-gray-900 font-medium">{new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}</div>
                                        </div>
                                        {invoice.dueDate && (
                                            <div>
                                                <div className="text-xs text-gray-500 font-medium uppercase">Due Date</div>
                                                <div className="text-gray-900 font-medium">{new Date(invoice.dueDate).toLocaleDateString('en-IN')}</div>
                                            </div>
                                        )}
                                        <div className="no-print">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                invoice.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                                                    invoice.status === 'PO' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                }`}>
                                                {invoice.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Line Items Table */}
                        <table className="w-full mb-6 text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-2 px-2 font-semibold text-xs text-gray-500 uppercase tracking-wider">Description</th>
                                    <th className="text-right py-2 px-2 font-semibold text-xs text-gray-500 uppercase tracking-wider w-20">Qty</th>
                                    <th className="text-right py-2 px-2 font-semibold text-xs text-gray-500 uppercase tracking-wider w-24">Rate</th>
                                    <th className="text-right py-2 px-2 font-semibold text-xs text-gray-500 uppercase tracking-wider w-28">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {invoice.lineItems.map((item, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-2 text-gray-900">{item.description}</td>
                                        <td className="py-2 px-2 text-gray-900 text-right">
                                            {parseFloat(item.quantity.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="py-2 px-2 text-gray-900 text-right">
                                            ₹{parseFloat(item.unitPrice.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="py-2 px-2 text-gray-900 text-right font-medium">
                                            ₹{parseFloat(item.amount.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div className="flex justify-end mb-8">
                            <div className="w-80">
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Subtotal:</span>
                                        <span className="font-medium">
                                            ₹{parseFloat(invoice.subtotal.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    {invoice.discountAmount > 0 && (
                                        <div className="flex justify-between text-sm text-red-600">
                                            <span>
                                                Discount {invoice.discountPercent > 0 ? `(${invoice.discountPercent}%)` : ''}:
                                            </span>
                                            <span className="font-medium">
                                                - ₹{parseFloat(invoice.discountAmount.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">GST ({invoice.taxPercent}%):</span>
                                        <span className="font-medium">
                                            ₹{parseFloat(invoice.taxAmount.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <div className="border-t-2 border-gray-300 pt-2 flex justify-between text-lg font-bold">
                                        <span>Total Amount:</span>
                                        <span>
                                            ₹{parseFloat(invoice.totalAmount.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        {invoice.notes && (
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">NOTES:</h3>
                                <div className="text-sm text-gray-600 whitespace-pre-line">
                                    {invoice.notes}
                                </div>
                            </div>
                        )}

                        {/* Terms & Conditions */}
                        {invoice.termsAndConditions && (
                            <div className="mb-8">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">TERMS & CONDITIONS:</h3>
                                <div className="text-sm text-gray-600 whitespace-pre-line">
                                    {invoice.termsAndConditions}
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="border-t border-gray-300 pt-6 mt-12">
                            <div className="text-center text-xs text-gray-500">
                                <p>This is a computer-generated document. No signature is required.</p>
                                <p className="mt-1">
                                    For any queries, please contact {invoice.businessSetting.email || invoice.businessSetting.phoneNumber}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
